/**
 * Encoding / decoding utilities for Custom Word Challenge mode.
 * Uses a simple reversible cipher + Base64 to obscure the word in the URL.
 * No backend needed — pure frontend.
 */

// Simple substitution key for light obfuscation
const CIPHER_KEY = 'WORDLE';

/**
 * XOR-style cipher using the key.
 * Works on Turkish uppercase characters.
 */
const xorCipher = (text, key) => {
  return [...text]
    .map((char, i) => {
      const keyChar = key[i % key.length];
      const xored = char.charCodeAt(0) ^ keyChar.charCodeAt(0);
      return String.fromCharCode(xored);
    })
    .join('');
};

/**
 * Encode a word into a URL-safe game code.
 * Steps:
 *  1. Uppercase the word (Turkish-aware)
 *  2. XOR cipher with key
 *  3. Base64 encode
 *  4. Make URL-safe
 */
export const encodeWordToGameCode = (word) => {
  const upper = word.toUpperCase().replace(/i/g, 'İ').replace(/ı/g, 'I');
  const ciphered = xorCipher(upper, CIPHER_KEY);
  // Use btoa with UTF-8 encoding support
  const base64 = btoa(unescape(encodeURIComponent(ciphered)));
  // Make URL-safe: replace + with -, / with _, remove =
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Decode a game code back into the original word.
 * Reverses the encoding process.
 */
export const decodeGameCodeToWord = (code) => {
  try {
    // Restore standard Base64
    let base64 = code.replace(/-/g, '+').replace(/_/g, '/');
    // Add back padding if needed
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    const decoded = decodeURIComponent(escape(atob(base64)));
    const word = xorCipher(decoded, CIPHER_KEY);
    return word.toUpperCase().replace(/i/g, 'İ').replace(/ı/g, 'I');
  } catch (err) {
    console.error('Failed to decode game code:', err);
    return null;
  }
};
